<script setup lang="ts">
import NavigationWrapper from '@/shared/components/core/GlobalActionsPanel/components/NavigationWrapper/NavigationWrapper.vue';
import PresentationPromo from '@/shared/components/core/PresentaitonPromo/PresentationPromo.vue';
import {
  IonApp,
  IonRouterOutlet,
  IonContent,
} from '@ionic/vue';
import BackgroundAudioPlayer from '@/shared/components/players/BackgroundAudioPlayer/BackgroundAudioPlayer.vue';
import {
  nextTick,
  onBeforeUnmount, onMounted, Ref, ref,
  watch,
} from 'vue';
import { MaybeNull } from '@/shared/types';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/core/store/main.ts';
import { useAppConfig } from '@/shared/composables/useAppConfig.ts';
import TosterCenter from '@/shared/components/core/TosterCenter/TosterCenter.vue';
import SideMenu from '@/shared/components/core/SideMenu.vue';
import LeaveFeedbackPopup from '@/shared/components/core/LeaveFeedbackReminder/LeaveFeedbackPopup.vue';
import { useLoaderAtStartStore } from '../components/loaders/LoaderAtStartApp/store';
import LoaderAtStartApp from '../components/loaders/LoaderAtStartApp/LoaderAtStartApp.vue';
import { useScreenActions } from '../components/core/ScreenActions/useScreenActions';

const deviceFrame$: Ref<MaybeNull<HTMLElement>> = ref(null);

const { desktopSlideContainerBorderRadiusPx, desktopSlideContainerHeightMarginPx } = useAppConfig();

const {
  desktopSlideContainerWidth,
  desktopSlideContainerHeight,
  windowWidth,
} = storeToRefs(useMainStore());

let deviceFrameResizeObserver: MaybeNull<ResizeObserver> = null;

const updateWidth = () => {
  if (!deviceFrame$.value) {
    return;
  }

  desktopSlideContainerWidth.value = deviceFrame$.value.offsetWidth;
  desktopSlideContainerHeight.value = deviceFrame$.value.offsetHeight;
  windowWidth.value = window.innerWidth;
};

const observeDeviceFrame = () => {
  deviceFrameResizeObserver?.disconnect();

  if (!deviceFrame$.value) {
    return;
  }

  deviceFrameResizeObserver = new ResizeObserver(updateWidth);
  deviceFrameResizeObserver.observe(deviceFrame$.value);
};

onMounted(() => {
  window.addEventListener('resize', updateWidth);
  observeDeviceFrame();
  updateWidth();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth);
  deviceFrameResizeObserver?.disconnect();
});

const { loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

const showContent = ref(false);

watch(() => loaderIsRequired.value, (value) => {
  if (!value) {
    showContent.value = true;
  }
}, {
  immediate: true,
});

watch(() => showContent.value, () => {
  nextTick(() => {
    observeDeviceFrame();
    updateWidth();
  });
}, {
  immediate: true,
});

const { mobileActionsTarget$, desktopActionsTarget$ } = useScreenActions();

</script>

<template>
  <div class="main-wrapper">
    <TosterCenter class="toster-center-component" />

    <PresentationPromo
      class="presentaiton-promo"
    />

    <SideMenu />

    <LeaveFeedbackPopup />

    <div ref="desktopActionsTarget$" />

    <div
      v-if="showContent"
      ref="deviceFrame$"
      class="device-frame"
    >
      <IonApp>
        <IonContent :force-overscroll="false">
          <div ref="mobileActionsTarget$" />
          <NavigationWrapper />
          <IonRouterOutlet />
        </IonContent>
      </IonApp>
    </div>

    <BackgroundAudioPlayer />

    <LoaderAtStartApp
      class="app-loader"
      @animated-complete="showContent = true"
    />
  </div>
</template>

<style scoped>
.main-wrapper {
  position: relative;
  height: 100%;
}

.device-frame {
  position: relative;
  flex-direction: column;
  height: 100dvh;
  z-index: 2;
  border-radius: v-bind('desktopSlideContainerBorderRadiusPx');
}

@media all and (min-width: 601px) {
  .device-frame {
    overflow: hidden;

    width: auto;
    height: calc((100vh - (2 * v-bind('desktopSlideContainerHeightMarginPx'))));
    margin: auto;
    margin-top: v-bind('desktopSlideContainerHeightMarginPx');
    margin-bottom: v-bind('desktopSlideContainerHeightMarginPx');
    max-width: calc(100vw - 200px);

    aspect-ratio: 9 / 16;
  }

  .ion-page,
  .toster-center-component,
  .presentaiton-promo {
    zoom: var(--zoom-for-desktop);
  }
}

.presentaiton-promo {
  z-index: 30;
}

ion-content {
  --background: rgba(27, 24, 28, 1);
}
</style>
